import { useEffect, useRef } from 'react';

type EventTarget = Window | Document | HTMLElement;

/**
 * Attach an event listener with automatic cleanup.
 * The handler ref is kept stable so the listener is only
 * re-attached when `target` or `eventName` change.
 */
export function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  target?: Window | null,
  options?: boolean | AddEventListenerOptions,
): void;
export function useEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  target: Document,
  options?: boolean | AddEventListenerOptions,
): void;
export function useEventListener<K extends keyof HTMLElementEventMap>(
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  target: HTMLElement | null,
  options?: boolean | AddEventListenerOptions,
): void;
export function useEventListener(
  eventName: string,
  handler: (event: Event) => void,
  target?: EventTarget | null,
  options?: boolean | AddEventListenerOptions,
): void;
export function useEventListener(
  eventName: string,
  handler: (event: any) => void,
  target?: EventTarget | null,
  options?: boolean | AddEventListenerOptions,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const el = target ?? window;
    if (!el?.addEventListener) return;

    const listener = (event: Event) => handlerRef.current(event);
    el.addEventListener(eventName, listener, options);
    return () => el.removeEventListener(eventName, listener, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, target]);
}
